import React, { useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Slider,
  Chip,
} from "@mui/material";
import { FaDownload } from "react-icons/fa";
import ChartSummaryRows from "./ChartSummaryRows";
import {
  getLocalDateOrFallback,
  getLocalHourOrFallback,
} from "../utils/localDateTime";

// ─── shared UI pieces ──────────────────────────────────────────────────────

const MultiSelect = ({ label, options, value, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <TextField
      select
      label={label}
      SelectProps={{
        multiple: true,
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        renderValue: (selected) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {selected.map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                onMouseDown={(e) => e.stopPropagation()}
                onDelete={() => onDelete(v)}
              />
            ))}
          </Box>
        ),
      }}
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
        setOpen(false);
      }}
      fullWidth
      size="small"
      sx={{ mt: 1 }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </TextField>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtSecs = (s) => {
  if (s == null || isNaN(s)) return "–";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};

const fmtDateLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = d
    .toLocaleString("en-GB", { month: "short", timeZone: "UTC" })
    .replace(".", "")
    .toUpperCase();
  const year = d.getUTCFullYear();
  return `${day}-${month}-${year}`.toUpperCase();
};

// Download records as CSV
const downloadCSV = (records) => {
  if (!records.length) return;
  const headers = Object.keys(records[0]);
  const rows = [
    headers.join(","),
    ...records.map((r) =>
      headers
        .map((h) => {
          const v = r[h] ?? "";
          const str = String(v).replace(/"/g, '""');
          return str.includes(",") || str.includes("\n") ? `"${str}"` : str;
        })
        .join(",")
    ),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "estimated_time_data.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Custom Tooltip ──────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload ?? {};
  const obs = row.observation_seconds ?? 0;
  const op = row.operation_seconds ?? 0;
  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 1.5,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
        {label}
      </Typography>
      <div style={{ color: "#5a8a2e" }}>Observationstid: {fmtSecs(obs)}</div>
      <div style={{ color: "#a8d44e" }}>Operationstid: {fmtSecs(op)}</div>
      <div style={{ fontWeight: 700, marginTop: 4 }}>
        Total: {fmtSecs(obs + op)}
      </div>
    </Box>
  );
};

// ─── Custom Label above bar ──────────────────────────────────────────────────

const TopLabel = (props) => {
  const { x, y, width, payload, rowData } = props;
  const rec = rowData ?? payload ?? {};
  const opText = `Op: ${fmtSecs(rec.operation_seconds ?? 0)}`;
  const obsText = `Obs: ${fmtSecs(rec.observation_seconds ?? 0)}`;
  const boxWidth = Math.max(opText.length, obsText.length) * 6.2 + 14;
  const boxHeight = 28;
  const boxX = x + width / 2 - boxWidth / 2;
  const boxY = Math.max(y - 38, 8);

  return (
    <g>
      <rect
        x={boxX}
        y={boxY}
        width={boxWidth}
        height={boxHeight}
        rx={6}
        ry={6}
        fill="rgba(255,255,255,0.92)"
      />
      <text
        x={x + width / 2}
        y={boxY + 11}
        textAnchor="middle"
        fontSize={9}
        fontWeight={600}
        fill="#555"
      >
        <tspan x={x + width / 2} dy="0">
          {opText}
        </tspan>
        <tspan x={x + width / 2} dy="11">
          {obsText}
        </tspan>
      </text>
    </g>
  );
};

// ─── Table ───────────────────────────────────────────────────────────────────

const COLS = [
  { key: "id", label: "ID" },
  { key: "date", label: "Dato" },
  { key: "beach", label: "Strand" },
  { key: "nb_set", label: "Sætnummer" },
  { key: "lifeguard", label: "Livredder" },
  { key: "supervisor", label: "Supervisor" },
  { key: "transportation", label: "Transport" },
  { key: "observation_time", label: "Obs. tid (s)" },
  { key: "operation_time", label: "Op. tid (s)" },
  { key: "qualityscore", label: "Score" },
  { key: "distance", label: "Distance" },
  { key: "comment", label: "Kommentar" },
];

const SortIcon = ({ active, dir }) => (
  <span style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4, lineHeight: 1, verticalAlign: "middle" }}>
    <span style={{ opacity: active && dir === "asc" ? 1 : 0.25, fontSize: 8, lineHeight: 1 }}>▲</span>
    <span style={{ opacity: active && dir === "desc" ? 1 : 0.25, fontSize: 8, lineHeight: 1 }}>▼</span>
  </span>
);

const DataTable = ({ records }) => {
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState("desc");

  const searched = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return records;
    return records.filter((r) =>
      COLS.some((col) => {
        const v = r[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [records, search]);

  const sorted = useMemo(
    () =>
      [...searched].sort((a, b) => {
        const av = a[sortKey] ?? "";
        const bv = b[sortKey] ?? "";
        const cmp =
          typeof av === "number"
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      }),
    [searched, sortKey, sortDir]
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const paginated = sorted.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const pageButtons = useMemo(() => {
    const start = Math.max(0, safePage - 2);
    const end = Math.min(totalPages - 1, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [safePage, totalPages]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
    setPage(0);
  };

  const thStyle = {
    padding: "10px 12px", textAlign: "center", fontWeight: 700, fontSize: 12,
    color: "#333", borderBottom: "2px solid #ddd", cursor: "pointer",
    whiteSpace: "nowrap", userSelect: "none", background: "#fafafa",
  };
  const tdStyle = {
    padding: "8px 12px", textAlign: "center", fontSize: 12,
    color: "#444", borderBottom: "1px solid #eee", verticalAlign: "middle",
    whiteSpace: "nowrap", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis",
  };
  const btnBase = { padding: "4px 10px", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer", fontSize: 12, background: "#fff", color: "#333" };
  const btnActive = { ...btnBase, background: "#c0392b", color: "#fff", borderColor: "#c0392b" };
  const btnDisabled = { ...btnBase, opacity: 0.45, cursor: "default" };

  return (
    <div style={{ fontFamily: "sans-serif", marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>Vis</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            style={{ padding: "3px 6px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13 }}
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>poster</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span>Søg:</span>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            style={{ padding: "4px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13, width: 200 }}
          />
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              {COLS.map((col) => (
                <th key={col.key} style={thStyle} onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon active={sortKey === col.key} dir={sortDir} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} style={{ ...tdStyle, padding: 28, color: "#999" }}>
                  Ingen poster fundet
                </td>
              </tr>
            ) : (
              paginated.map((row, i) => (
                <tr key={row.id} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                  {COLS.map((col) => (
                    <td key={col.key} style={tdStyle} title={String(row[col.key] ?? "")}>
                      {row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 13 }}>
        <span style={{ color: "#555" }}>
          {sorted.length === 0
            ? "Viser 0 poster"
            : `Viser ${safePage * pageSize + 1} til ${Math.min((safePage + 1) * pageSize, sorted.length)} af ${sorted.length} poster`}
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button style={safePage === 0 ? btnDisabled : btnBase} disabled={safePage === 0} onClick={() => setPage((p) => p - 1)}>Forrige</button>
          {pageButtons.map((p) => (
            <button key={p} style={p === safePage ? btnActive : btnBase} onClick={() => setPage(p)}>{p + 1}</button>
          ))}
          <button style={safePage >= totalPages - 1 ? btnDisabled : btnBase} disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Næste</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * EstimatedTimeChart
 *
 * Props:
 *  - data          : array of records from the API response (the `data` field)
 *  - organizationId: number — chart + tab only visible when === 19
 *  - lang          : "dk" | "en"  (default "dk")
 *
 * Usage (inside the parent that has the tab set):
 *   <EstimatedTimeChart data={apiResponse.data} organizationId={orgId} lang={lang} />
 *
 * The component handles its own filters (date range, time-of-day slider,
 * set-number text, beach dropdown) independently.
 */
const EstimatedTimeChart = ({
  data = [],
  organizationId,
  lang = "dk",
  hideFilters = false,
  externalFilters = null,
}) => {
  // ── Guard: only visible for org 19 ──────────────────────────────────────
  if (organizationId !== 19) return null;

  // ── Filter state ─────────────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [timeRange, setTimeRange] = useState([0, 24]);
  const [setNumbers, setSetNumbers] = useState([]);
  const [beach, setBeach] = useState("");

  const merged = externalFilters ?? {
    dateFrom,
    dateTo,
    timeRange,
    setNumbers,
    beach,
    setDateFrom,
    setDateTo,
    setTimeRange,
    setSetNumbers,
    setBeach,
  };

  // Derived beach list from data
  const beachOptions = useMemo(() => {
    const s = new Set(data.map((r) => r.beach).filter(Boolean));
    return Array.from(s).sort();
  }, [data]);

  const setNumberOptions = useMemo(() => {
    const raw = data
      .map((r) => r?.nb_set)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
    const uniq = Array.from(new Set(raw.map((v) => String(v))));
    uniq.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
    return uniq;
  }, [data]);

  // Filtered records
  const filtered = useMemo(() => {
    return data.filter((r) => {
      const recDate = getLocalDateOrFallback(r.utc_scanned, r.date);
      if (merged.dateFrom && recDate < merged.dateFrom) return false;
      if (merged.dateTo && recDate > merged.dateTo) return false;
      if (merged.setNumbers.length > 0 && !merged.setNumbers.includes(String(r.nb_set))) return false;
      if (merged.beach && r.beach !== merged.beach) return false;
      const hour = getLocalHourOrFallback(r.utc_scanned);
      if (hour !== null && (hour < merged.timeRange[0] || hour > merged.timeRange[1])) return false;
      return true;
    });
  }, [data, merged]);

  // Chart data — one bar per record, sorted by date
  const chartData = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
        .map((r) => ({
          ...r,
          label: fmtDateLabel(r.date),
          // recharts needs numeric values
          observation_time: r.observation_time ?? 0,
          operation_time: r.operation_time ?? 0,
        })),
    [filtered]
  );

  // Averages for legend
  const avgObs = useMemo(() => {
    if (!chartData.length) return 0;
    return chartData.reduce((s, r) => s + r.observation_time, 0) / chartData.length;
  }, [chartData]);

  const avgOp = useMemo(() => {
    if (!chartData.length) return 0;
    return chartData.reduce((s, r) => s + r.operation_time, 0) / chartData.length;
  }, [chartData]);

  const avgTotal = avgObs + avgOp;

  const sliderMarks = Array.from({ length: 9 }, (_, i) => ({ value: i * 3, label: String(i * 3) }));

  const T = {
    title: lang === "dk" ? "Redningstider (Observation + Operation)" : "Rescue Times (Observation + Operation)",
    timePeriod: lang === "dk" ? "Vælg tidsperiode:" : "Select time period:",
    timeOfDay: lang === "dk" ? "Vælg tidspunkt på døgnet:" : "Select time of day:",
    setNum: lang === "dk" ? "Sætnummer" : "Set number",
    beachLbl: lang === "dk" ? "Vælg beach" : "Select beach",
    downloadCsv: lang === "dk" ? "Download CSV" : "Download CSV",
    opAvg: lang === "dk" ? "Operationstid" : "Operation time",
    obsAvg: lang === "dk" ? "Observationstid" : "Observation time",
    yAxis: lang === "dk" ? "Redningstid (minutter)" : "Rescue time (minutes)",
    noData: lang === "dk" ? "Ingen data matcher de valgte filtre." : "No data matches the selected filters.",
  };

  // Convert seconds → minutes for Y axis
  const toMin = (s) => +(s / 60).toFixed(3);

  const chartDataMin = useMemo(
    () => {
      const mapped = chartData.map((r) => ({
        ...r,
        observation_seconds: r.observation_time ?? 0,
        operation_seconds: r.operation_time ?? 0,
        observation_time: toMin(r.observation_time),
        operation_time: toMin(r.operation_time),
      }));
      if (mapped.length === 0) {
        return [
          {
            label: "Ingen data",
            observation_seconds: 0,
            operation_seconds: 0,
            observation_time: 0,
            operation_time: 0,
            date: merged.dateFrom || merged.dateTo || "N/A",
          },
        ];
      }
      return mapped;
    },
    [chartData]
  );

  const summaryRows = useMemo(
    () => [
      { label: T.obsAvg, value: fmtSecs(avgObs), color: "#6aaa1e" },
      { label: T.opAvg, value: fmtSecs(avgOp), color: "#b5e55a" },
      {
        label: lang === "dk" ? "Samlet gns." : "Total avg.",
        value: fmtSecs(avgTotal),
        color: "#4f7f1f",
      },
    ],
    [T.obsAvg, T.opAvg, avgObs, avgOp, avgTotal, lang]
  );

  return (
    <Box sx={{ mt: 2 }}>
      {/* ── Filters ── */}
      {!hideFilters && (
        <>
      <div className="row g-3 mb-3">
        {/* Date range */}
        <div className="col-md-4">
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {T.timePeriod}
          </Typography>
          <div className="d-flex gap-2">
            <TextField
              type="date"
              size="small"
              fullWidth
              value={merged.dateFrom}
              onChange={(e) => merged.setDateFrom(e.target.value)}
              inputProps={{ style: { fontSize: 13 } }}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              value={merged.dateTo}
              onChange={(e) => merged.setDateTo(e.target.value)}
              inputProps={{ style: { fontSize: 13 } }}
            />
          </div>
        </div>

        {/* Set number */}
        <div className="col-md-2">
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {T.setNum}
          </Typography>
          <MultiSelect
            label=""
            options={setNumberOptions}
            value={merged.setNumbers}
            onChange={(sel) => merged.setSetNumbers(sel)}
            onDelete={(v) => merged.setSetNumbers((p) => p.filter((x) => x !== v))}
          />
        </div>

        {/* Download CSV */}
        <div className="col-12 col-lg-3 d-flex align-items-end">
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<FaDownload />}
            onClick={() => downloadCSV(filtered)}
            sx={{
              borderColor: "#c0392b",
              color: "#c0392b",
              "&:hover": { borderColor: "#922b21", color: "#922b21", background: "#fdf2f2" },
              textTransform: "none",
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            {T.downloadCsv}
          </Button>
        </div>
      </div>

      <div className="row g-3 mb-3">
        {/* Time slider */}
        <div className="col-12 col-lg-4">
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {T.timeOfDay}
          </Typography>
          <Slider
            value={merged.timeRange}
            onChange={(_, v) => merged.setTimeRange(v)}
            valueLabelDisplay="on"
            min={0}
            max={24}
            marks={sliderMarks}
            sx={{
              mt: 3,
              color: "#c0392b",
              "& .MuiSlider-valueLabel": { backgroundColor: "#555", color: "#fff" },
            }}
          />
        </div>

        {/* Beach dropdown */}
        <div className="col-12 col-lg-3">
          <Typography variant="body2" fontWeight={700} gutterBottom>
            {T.beachLbl}
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={merged.beach}
            onChange={(e) => merged.setBeach(e.target.value)}
            inputProps={{ style: { fontSize: 13 } }}
          >
            <MenuItem value="">
              <em>{lang === "dk" ? "Alle strande" : "All beaches"}</em>
            </MenuItem>
            {beachOptions.map((b) => (
              <MenuItem key={b} value={b}>{b}</MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      <hr style={{ borderColor: "#c0392b", margin: "12px 0 16px" }} />
      </>
      )}

      {/* ── Chart ── */}
      <Box sx={{ background: "#fff", borderRadius: 2, p: 2, border: "1px solid #eee" }}>
        <Typography variant="subtitle1" fontWeight={700} textAlign="center" mb={1}>
          {T.title}
        </Typography>

        <div style={{ width: "100%", height: 600 }}>
          <ChartSummaryRows rows={summaryRows} />
          <ResponsiveContainer width="100%" height="86%">
            <BarChart
              data={chartDataMin}
              margin={{ top: 72, right: 30, left: 20, bottom: 60 }}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#666" }}
                angle={-90}
                textAnchor="end"
                interval={0}
                height={110}
                tickMargin={12}
              />
              <YAxis
                label={{
                  value: T.yAxis,
                  angle: -90,
                  position: "insideLeft",
                  offset: -4,
                  style: { fontSize: 12, fill: "#666" },
                }}
                tick={{ fontSize: 11, fill: "#666" }}
                tickFormatter={(v) => v.toFixed(1)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="observation_time" stackId="a" fill="#6aaa1e" name="observation_time" isAnimationActive={false} />
              <Bar dataKey="operation_time" stackId="a" fill="#b5e55a" name="operation_time" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                <LabelList
                  content={(props) => (
                    <TopLabel
                      {...props}
                      rowData={chartDataMin[props.index]}
                    />
                  )}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Box>

      {/* ── Table ── */}
      <Box mt={3}>
        <hr style={{ borderColor: "#c0392b", margin: "16px 0" }} />
        <DataTable records={filtered} />
      </Box>
    </Box>
  );
};

export default EstimatedTimeChart;
